import React, { useEffect, useState } from "react";
import {
  Radar,
  Briefcase,
  CalendarDays,
  Home as HomeIcon,
  Loader2,
  Download,
  Upload,
  CheckCircle,
  AlertTriangle,
  History,
  ExternalLink,
  Pencil,
  Plus,
  X,
  Save,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PRESET_ICONS = { jobs: Briefcase, events: CalendarDays, properties: HomeIcon };

const LocalSignals = () => {
  const [presets, setPresets] = useState(null);
  const [preset, setPreset] = useState("jobs");
  const [region, setRegion] = useState("paisley_renfrewshire");
  const [pushDrive, setPushDrive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [sources, setSources] = useState([]);
  const [customized, setCustomized] = useState(false);
  const [editing, setEditing] = useState(false);
  const [savingSources, setSavingSources] = useState(false);

  const loadPresets = async () => {
    try {
      const { data } = await api.get("/signals/presets");
      setPresets(data);
    } catch {}
  };
  const loadHistory = async () => {
    try {
      const { data } = await api.get("/signals/history");
      setHistory(data || []);
    } catch {}
  };
  useEffect(() => {
    loadPresets();
    loadHistory();
  }, []);

  // When preset changes, pick the first available region
  useEffect(() => {
    if (presets && presets[preset]) {
      const firstRegion = Object.keys(presets[preset].regions)[0];
      setRegion(firstRegion);
    }
  }, [preset, presets]);

  // Load the (possibly customised) source list for the current preset+region
  useEffect(() => {
    if (!presets || !presets[preset] || !region) return;
    setEditing(false);
    api
      .get("/signals/sources", { params: { preset, region } })
      .then(({ data }) => {
        setSources(data.sources || []);
        setCustomized(!!data.custom);
      })
      .catch(() => {
        setSources(presets[preset]?.regions?.[region]?.sources || []);
        setCustomized(false);
      });
  }, [preset, region, presets]);

  const updateSource = (i, key, val) =>
    setSources((cur) => cur.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  const removeSource = (i) => setSources((cur) => cur.filter((_, idx) => idx !== i));
  const addSource = () => setSources((cur) => [...cur, { name: "", url: "" }]);

  const saveSources = async () => {
    const clean = sources.filter((s) => (s.url || "").trim());
    if (clean.length === 0) return toast.error("Add at least one URL.");
    setSavingSources(true);
    try {
      const { data } = await api.post("/signals/sources", { preset, region, sources: clean });
      setSources(data.sources);
      setCustomized(true);
      setEditing(false);
      toast.success("Sources saved for this region.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save sources.");
    } finally {
      setSavingSources(false);
    }
  };

  const resetSources = async () => {
    setSavingSources(true);
    try {
      const { data } = await api.post("/signals/sources/reset", { preset, region, sources: [] });
      setSources(data.sources);
      setCustomized(false);
      setEditing(false);
      toast.success("Reset to default sources.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset.");
    } finally {
      setSavingSources(false);
    }
  };

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/signals/scrape", {
        preset,
        region,
        custom_sources: sources.filter((s) => (s.url || "").trim()),
        push_to_drive: pushDrive,
      });
      setResult(data);
      const okCount = (data.source_status || []).filter((s) => s.ok).length;
      toast.success(
        `${data.row_count} rows from ${okCount}/${(data.source_status || []).length} sources.`
      );
      if (data.drive_file) toast.success("Pushed to Google Drive.");
      if (data.drive_error) toast.error(`Drive: ${data.drive_error}`);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape failed.");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!result?.csv_base64) return;
    try {
      const bin = atob(result.csv_base64);
      const blob = new Blob([bin], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename || "clydeflow-signals.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not build CSV blob.");
    }
  };

  const currentPreset = presets?.[preset];
  const currentRegion = currentPreset?.regions?.[region];

  return (
    <div className="space-y-6" data-testid="signals-panel">
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Radar size={22} className="text-cyan-300" />
          <h2 className="font-heading text-2xl font-bold">Local Signals</h2>
        </div>
        <p className="text-slate-400 text-sm max-w-2xl">
          Pull live jobs, events, and properties from local UK sites in one shot.
          Pick a preset, hit Run, get a clean CSV — download it or have it dropped
          straight into your Drive.
        </p>
      </div>

      {/* Preset chips */}
      <div className="flex flex-wrap gap-2">
        {presets &&
          Object.entries(presets).map(([id, cfg]) => {
            const Icon = PRESET_ICONS[id] || Radar;
            const active = id === preset;
            return (
              <button
                key={id}
                onClick={() => setPreset(id)}
                data-testid={`signals-preset-${id}`}
                className={[
                  "px-4 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest border flex items-center gap-2",
                  active
                    ? "bg-cyan-400/15 border-cyan-400 text-cyan-200"
                    : "bg-[#0A192F] border-white/10 text-slate-300 hover:border-cyan-400/40",
                ].join(" ")}
              >
                <Icon size={12} /> {cfg.label}
              </button>
            );
          })}
      </div>

      {/* Region select */}
      {currentPreset && (
        <div className="flex flex-wrap items-center gap-3">
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
            Region:
          </label>
          <select
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            data-testid="signals-region"
            className="px-3 py-1.5 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-xs rounded-sm"
          >
            {Object.entries(currentPreset.regions).map(([rid, r]) => (
              <option key={rid} value={rid}>{r.label}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
            <input
              type="checkbox"
              checked={pushDrive}
              onChange={(e) => setPushDrive(e.target.checked)}
              data-testid="signals-push-drive"
            />
            <Upload size={11} /> Push to Drive
          </label>
          <button
            onClick={run}
            disabled={busy}
            data-testid="signals-run"
            className="ml-auto px-5 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF] disabled:opacity-60 flex items-center gap-2"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />}
            {busy ? "Scanning…" : "Run Scan"}
          </button>
        </div>
      )}

      {/* Sources — view + edit */}
      {currentRegion && !result && (
        <div className="p-4 bg-[#0A192F] border border-white/10 rounded-sm" data-testid="signals-sources">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Sources for {currentRegion.label}
              {customized && (
                <span className="ml-2 text-cyan-300">· customised</span>
              )}
            </p>
            {!editing ? (
              <button
                onClick={() => setEditing(true)}
                data-testid="signals-edit-sources"
                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10"
              >
                <Pencil size={11} /> Edit sources
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={resetSources}
                  disabled={savingSources}
                  data-testid="signals-reset-sources"
                  className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 border border-white/15 text-slate-300 hover:bg-white/5 disabled:opacity-60"
                  title="Reset to default sources"
                >
                  <RotateCcw size={11} /> Reset
                </button>
                <button
                  onClick={saveSources}
                  disabled={savingSources}
                  data-testid="signals-save-sources"
                  className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 bg-[#00F0FF] text-[#050B14] hover:bg-[#33F3FF] disabled:opacity-60"
                >
                  {savingSources ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                </button>
              </div>
            )}
          </div>

          {!editing ? (
            <div className="flex flex-wrap gap-2">
              {sources.map((s) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-2 py-1 bg-[#050B14] border border-white/10 hover:border-cyan-400/40 text-slate-300 hover:text-cyan-300 text-[10px] font-mono uppercase tracking-widest rounded-sm flex items-center gap-1"
                >
                  {s.name} <ExternalLink size={9} />
                </a>
              ))}
              {sources.length === 0 && (
                <p className="text-xs text-slate-500">No sources yet — click “Edit sources” to add some.</p>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="signals-sources-editor">
              {sources.map((s, i) => (
                <div key={i} className="flex flex-col sm:flex-row gap-2">
                  <input
                    value={s.name}
                    onChange={(e) => updateSource(i, "name", e.target.value)}
                    placeholder="Label (e.g. OneRen Jobs)"
                    data-testid={`signals-source-name-${i}`}
                    className="sm:w-1/3 px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-white text-xs rounded-sm"
                  />
                  <input
                    value={s.url}
                    onChange={(e) => updateSource(i, "url", e.target.value)}
                    placeholder="https://..."
                    data-testid={`signals-source-url-${i}`}
                    className="flex-1 px-3 py-2 bg-[#050B14] border border-white/10 focus:border-cyan-400 outline-none text-cyan-50 text-xs rounded-sm font-mono"
                  />
                  <button
                    onClick={() => removeSource(i)}
                    data-testid={`signals-source-remove-${i}`}
                    className="px-3 py-2 border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 rounded-sm shrink-0"
                    title="Remove source"
                  >
                    <X size={13} />
                  </button>
                </div>
              ))}
              <button
                onClick={addSource}
                data-testid="signals-add-source"
                className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider rounded-sm flex items-center gap-1 border border-dashed border-white/20 text-slate-300 hover:border-cyan-400/40 hover:text-cyan-300"
              >
                <Plus size={12} /> Add source
              </button>
              <p className="text-[10px] text-slate-500 font-mono pt-1">
                Tip: point each source at a page that actually lists the items (e.g. a careers or “what’s on” page). Saved per region.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Busy state */}
      {busy && (
        <div className="p-6 bg-[#0A192F] border border-cyan-400/30 cf-glow rounded-sm text-center" data-testid="signals-busy">
          <Loader2 size={28} className="text-cyan-300 animate-spin mx-auto" />
          <p className="mt-3 text-white font-heading font-bold">Pulling signals…</p>
          <p className="mt-1 text-xs text-slate-400 font-mono uppercase tracking-widest">
            Hitting {sources.length} sources in parallel · ~30s
          </p>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4" data-testid="signals-result">
          <div className="p-4 bg-[#0A192F] border border-emerald-400/30 rounded-sm flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-heading font-bold text-white">
                {result.row_count} rows · {result.filename}
              </p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                Sources: {(result.source_status || []).filter((s) => s.ok).length} ok ·
                {" "}{(result.source_status || []).filter((s) => !s.ok).length} failed
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={download}
                data-testid="signals-download"
                className="px-4 py-2 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF] flex items-center gap-2"
              >
                <Download size={14} /> Download CSV
              </button>
              {result.drive_file?.webViewLink && (
                <a
                  href={result.drive_file.webViewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="signals-drive-link"
                  className="px-4 py-2 border border-cyan-400/40 text-cyan-300 font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-cyan-400/10 flex items-center gap-2"
                >
                  Open in Drive <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>

          {/* Source diagnostics */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(result.source_status || []).map((s) => (
              <div
                key={s.url}
                className={`px-3 py-2 bg-[#050B14] border rounded-sm flex items-start gap-2 ${
                  s.ok ? "border-emerald-400/20" : "border-amber-400/20"
                }`}
              >
                {s.ok ? (
                  <CheckCircle size={13} className="text-emerald-300 mt-0.5 shrink-0" />
                ) : (
                  <AlertTriangle size={13} className="text-amber-300 mt-0.5 shrink-0" />
                )}
                <div className="min-w-0">
                  <p className="text-slate-200 text-xs truncate">{s.source}</p>
                  {!s.ok && (
                    <p className="text-[10px] text-amber-200 font-mono">{s.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Preview table */}
          {(result.rows || []).length > 0 && (
            <div className="bg-[#0A192F] border border-white/10 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-500 border-b border-white/10">
                    {Object.keys(result.rows[0] || {}).slice(0, 6).map((k) => (
                      <th key={k} className="px-3 py-2">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, 25).map((r, i) => (
                    <tr key={i} className="border-b border-white/5">
                      {Object.keys(result.rows[0] || {}).slice(0, 6).map((k) => (
                        <td key={k} className="px-3 py-2 text-slate-200 max-w-xs truncate">
                          {String(r[k] ?? "")}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {result.rows.length > 25 && (
                <p className="px-3 py-2 text-[10px] text-slate-500 font-mono">
                  Showing 25 of {result.rows.length} — download the CSV for the full set.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <History size={14} className="text-slate-500" />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">
              Recent scans
            </p>
          </div>
          <div className="space-y-1">
            {history.slice(0, 12).map((h) => (
              <div
                key={h.job_id}
                data-testid={`signals-history-${h.job_id}`}
                className="px-3 py-2 bg-[#0A192F] border border-white/5 rounded-sm flex items-center justify-between text-sm"
              >
                <div className="min-w-0">
                  <p className="text-slate-200 truncate">{h.filename}</p>
                  <p className="text-[10px] font-mono text-slate-500">
                    {h.row_count} rows · {h.preset} · {h.region} ·
                    {" "}{h.created_at?.slice(0, 16).replace("T", " ")}
                  </p>
                </div>
                {h.drive_file?.webViewLink && (
                  <a
                    href={h.drive_file.webViewLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 hover:text-cyan-200"
                  >
                    Drive ↗
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocalSignals;
